import React, { useState, useEffect, useMemo } from 'react';
import {
  StyleSheet, Text, View, TextInput, TouchableOpacity, ScrollView, ActivityIndicator, Modal, KeyboardAvoidingView, Platform,
} from 'react-native';
import { useIsFocused } from '@react-navigation/native';
import { Ionicons } from '@expo/vector-icons';
import Data, { authHeader } from './Data';
import { getStorage } from '../storage';
import { useScale, ScaleFn, TablePagination } from './common';

const C = {
  bg: '#F9FAFB', card: '#FFFFFF', cardInner: '#F3F4F6',
  border: '#E5E7EB', text: '#111827', textSub: '#4B5563',
  muted: '#9CA3AF', accent: '#38BDF8', ok: '#34D399', warn: '#FB923C', err: '#EF4444',
};

type Rec = {
  IDX: number; PERSON_IDX: number; PERSON_NAME: string;
  TYPE: string; SUB_TYPE: string | null; STATUS: number | null; AMOUNT: string | null;
  RECORDER: string; REGDATE: string;
  PENDING_REQ?: number; EDIT_COUNT?: number;   // 요청중/수정됨 뱃지용
};

type ReqDetail = {
  IDX: number; REQ_TYPE: string; STATUS: string; REASON: string;
  REQUESTER: string; REGDATE: string; RESOLVED_AT: string | null; RESOLVER: string | null; ADMIN_MEMO: string | null;
  OLD_TYPE: string | null; OLD_SUB_TYPE: string | null; OLD_STATUS: number | null; OLD_AMOUNT: string | null; OLD_RECORDER: string | null;
  NEW_TYPE: string | null; NEW_SUB_TYPE: string | null; NEW_STATUS: number | null; NEW_AMOUNT: string | null; NEW_RECORDER: string | null;
};

const STATUS_LABEL: Record<number, string> = { 0: '없음', 1: '욕창', 2: '욕창위험', 4: '요로감염' };
const fmtStatus = (s: number | null | undefined) => (s == null ? '-' : (STATUS_LABEL[s] ?? '-'));
type Person = { IDX: number; NAME: string; AREA: string; ROOM_NO: string };

const fmtDate = (d: Date) => d.toISOString().slice(0, 10);
const today = new Date();
const monthAgo = new Date(); monthAgo.setMonth(monthAgo.getMonth() - 1);

export default function DiaperViewer() {
  const isFocused = useIsFocused();
  const { scale, s } = useScale();
  const styles = useMemo(() => makeStyles(s), [scale]);
  const [persons, setPersons] = useState<Person[]>([]);
  const [personFilter, setPersonFilter] = useState<string>('all');
  const [personSearch, setPersonSearch] = useState<string>('');
  const [expandedArea, setExpandedArea] = useState<string | null>(null);
  const [startDate, setStartDate] = useState<string>(fmtDate(monthAgo));
  const [endDate, setEndDate] = useState<string>(fmtDate(today));
  const [records, setRecs] = useState<Rec[]>([]);
  // 목록은 서버에서 한 번에 받아 화면에서 쪽 단위로 자른다(다른 화면과 같은 방식).
  const [page, setPage] = useState(1);
  const [perPage, setPerPage] = useState(100);
  const _totalPages = Math.max(1, Math.ceil((records || []).length / perPage));
  const _curPage = Math.min(Math.max(1, page), _totalPages);
  const pageData = (records || []).slice((_curPage - 1) * perPage, _curPage * perPage);
  const [loading, setLoading] = useState(false);
  const [admin, setAdmin] = useState('1');
  useEffect(() => { getStorage('AutoLogin').then((u: any) => setAdmin(u?.admin ?? '1')); }, []);
  const downloadCsv = async () => {
    if (personFilter === 'all') { alert('환자를 먼저 선택하세요'); return; }
    try {
      const resp = await fetch('sensor/personRawCsv', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', ...(await authHeader()) },
        body: JSON.stringify({ PERSON_IDX: parseInt(personFilter), startDate: startDate + ' 00:00:00', endDate: endDate + ' 23:59:59' }),
      });
      if (!resp.ok) { alert((await resp.text()) || '다운로드 실패'); return; }
      const blob = await resp.blob();
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      const pname = persons.find(p => String(p.IDX) === personFilter)?.NAME || personFilter;
      link.download = `${pname}_raw_${startDate}_${endDate}.csv`;
      document.body.appendChild(link); link.click(); document.body.removeChild(link);
      window.URL.revokeObjectURL(url);
    } catch (e) { alert('다운로드 중 오류'); }
  };

  // 수정 요청 모달
  const [editing, setEditing] = useState<Rec | null>(null);
  const [reqReason, setReqReason] = useState('');
  const [reqType, setReqType] = useState<string>('');
  const [reqSubType, setReqSubType] = useState<string>('');
  const [reqStatus, setReqStatus] = useState<number | null>(null);
  const [reqAmount, setReqAmount] = useState('');
  const [reqRecorder, setReqRecorder] = useState('');
  const [reqRequester, setReqRequester] = useState('');
  const [reqMsg, setReqMsg] = useState<{ text: string; err: boolean } | null>(null);
  const [reqSubmitting, setReqSubmitting] = useState(false);
  // 요청 내역(변경 전→후) 상세 보기
  const [reqDetailRec, setReqDetailRec] = useState<Rec | null>(null);
  const [reqDetailData, setReqDetailData] = useState<ReqDetail[]>([]);
  const [reqDetailLoading, setReqDetailLoading] = useState(false);

  const openReqDetail = (r: Rec) => {
    setReqDetailRec(r);
    setReqDetailData([]);
    setReqDetailLoading(true);
    Data.fetchData('diaper/recordRequests', { RECORD_IDX: r.IDX }, (result: ReqDetail[]) => {
      setReqDetailData(result || []);
      setReqDetailLoading(false);
    });
  };

  const openEdit = (r: Rec) => {
    setEditing(r);
    setReqReason('');
    setReqType('');
    setReqSubType('');
    setReqStatus(null);
    setReqAmount('');
    setReqRecorder('');
    setReqRequester('');
    setReqMsg(null);
  };
  const closeEdit = () => { setEditing(null); setReqMsg(null); };

  const submitRequest = () => {
    if (reqSubmitting || !editing) return;
    if (!reqReason.trim()) { setReqMsg({ text: '사유를 입력해 주세요', err: true }); return; }
    if (!reqRequester.trim()) { setReqMsg({ text: '요청자 이름을 입력해 주세요', err: true }); return; }
    if (!/^[가-힣]{2,5}$/.test(reqRequester.trim())) {
      setReqMsg({ text: '요청자 이름은 한글 2~5자', err: true }); return;
    }
    if (reqAmount.trim()) {
      const n = parseFloat(reqAmount.trim());
      if (isNaN(n) || n <= 0 || n > 5000) {
        setReqMsg({ text: '수정 양은 0 초과 5000 이하', err: true }); return;
      }
    }
    if (reqRecorder.trim() && !/^[가-힣]{2,5}$/.test(reqRecorder.trim())) {
      setReqMsg({ text: '수정 기록자 이름은 한글 2~5자', err: true }); return;
    }
    setReqSubmitting(true);
    Data.fetchData('diaper/requestEdit', {
      RECORD_IDX: editing.IDX,
      REASON: reqReason.trim(),
      NEW_TYPE: reqType || null,
      NEW_SUB_TYPE: (reqType === '대변') ? (reqSubType || null) : null,
      NEW_STATUS: reqStatus,
      NEW_AMOUNT: reqAmount.trim() || null,
      NEW_RECORDER: reqRecorder.trim() || null,
      REQUESTER: reqRequester.trim(),
    }, (result: any) => {
      setReqSubmitting(false);
      if (result && result.IDX) {
        closeEdit();
        load();   // 리스트 갱신 → "요청중" 뱃지 즉시 표시 (새로고침 불필요)
      } else {
        setReqMsg({ text: '요청 실패 — 서버 응답 오류', err: true });
      }
    }, () => { setReqSubmitting(false); setReqMsg({ text: '요청 실패 — 네트워크 오류', err: true }); });
  };

  // 삭제 요청 — 사유 + 요청자만 필요 (수정안 항목 무시), 관리자 승인 후 소프트삭제
  const submitDelete = () => {
    if (reqSubmitting || !editing) return;
    if (!reqReason.trim()) { setReqMsg({ text: '삭제 사유를 입력해 주세요', err: true }); return; }
    if (!reqRequester.trim() || !/^[가-힣]{2,5}$/.test(reqRequester.trim())) {
      setReqMsg({ text: '요청자 이름은 한글 2~5자', err: true }); return;
    }
    if (typeof window !== 'undefined' && typeof window.confirm === 'function' &&
        !window.confirm('이 기록의 삭제를 요청합니다.\n관리자 승인 시 목록에서 제거됩니다. 계속할까요?')) return;
    setReqSubmitting(true);
    Data.fetchData('diaper/requestDelete', {
      RECORD_IDX: editing.IDX,
      REASON: reqReason.trim(),
      REQUESTER: reqRequester.trim(),
    }, (result: any) => {
      setReqSubmitting(false);
      if (result && result.IDX) {
        closeEdit();
        load();   // 리스트 갱신 → "요청중" 뱃지 즉시 표시 (새로고침 불필요)
      } else {
        setReqMsg({ text: '삭제 요청 실패 — 서버 응답 오류', err: true });
      }
    }, () => { setReqSubmitting(false); setReqMsg({ text: '삭제 요청 실패 — 네트워크 오류', err: true }); });
  };

  // 이용자 목록
  useEffect(() => {
    if (!isFocused) return;
    Data.fetchData('diaper/personList', {}, (result: Person[]) => setPersons(result || []));
  }, [isFocused]);

  const load = () => {
    setLoading(true);
    const body: any = { STARTDATE: startDate, ENDDATE: endDate };
    if (personFilter !== 'all') body.PERSON_IDX = parseInt(personFilter);
    Data.fetchData('diaper/list', body, (result: Rec[]) => {
      setRecs(result || []);
      setPage(1);
      setLoading(false);
    }, () => setLoading(false));
  };

  useEffect(() => { if (isFocused) load(); }, [isFocused, personFilter]);

  // 집계 통계
  const stats = useMemo(() => {
    const urine = records.filter(r => r.TYPE === '소변').length;
    const stool = records.filter(r => r.TYPE === '대변' || r.TYPE === '대,소변').length;
    const byRecorder = records.reduce<Record<string, number>>((m, r) => {
      m[r.RECORDER] = (m[r.RECORDER] || 0) + 1; return m;
    }, {});
    return { urine, stool, byRecorder };
  }, [records]);

  // 구역별 그룹 (아코디언용, 순서 보존)
  const personGroups = useMemo(() => {
    const map: Record<string, Person[]> = {};
    const order: string[] = [];
    persons.forEach(p => {
      const area = p.AREA || '기타';
      if (!map[area]) { map[area] = []; order.push(area); }
      map[area].push(p);
    });
    return order.map(area => ({ area, list: map[area] }));
  }, [persons]);

  return (
    <View style={[styles.root, { backgroundColor: C.bg }]}>

      {/* 이용자 검색 섹션 */}
      <View style={styles.userSection}>
        <View style={styles.userSectionHeader}>
          <Ionicons name="people-outline" size={s(22)} color={C.accent} />
          <Text style={styles.userSectionTitle}>이용자 검색</Text>
          {personFilter !== 'all' && (
            <View style={styles.selectedBadge}>
              <Text style={styles.selectedBadgeText}>
                선택: {persons.find(p => String(p.IDX) === personFilter)?.NAME || ''}
              </Text>
              <TouchableOpacity onPress={() => { setPersonFilter('all'); setPersonSearch(''); }} hitSlop={{ top: 8, bottom: 8, left: 8, right: 8 }}>
                <Ionicons name="close-circle" size={s(18)} color="#FFF" />
              </TouchableOpacity>
            </View>
          )}
        </View>
        <TextInput
          value={personSearch}
          onChangeText={setPersonSearch}
          placeholder="이용자 이름 입력 (예: 김OO)"
          placeholderTextColor={C.muted}
          style={styles.userSearch}
        />
        {personSearch.trim().length > 0 ? (
          <ScrollView style={styles.resultList} nestedScrollEnabled keyboardShouldPersistTaps="handled">
            {persons
              .filter(p => p.NAME.includes(personSearch.trim()))
              .slice(0, 30)
              .map(p => (
                <TouchableOpacity
                  key={p.IDX}
                  onPress={() => { setPersonFilter(String(p.IDX)); setPersonSearch(''); }}
                  style={[styles.resultRow, personFilter === String(p.IDX) && styles.resultRowActive]}
                  activeOpacity={0.6}
                >
                  <Text style={styles.resultName}>{p.NAME}</Text>
                  <Text style={styles.resultMeta}>{p.AREA}{p.ROOM_NO ? ' · ' + p.ROOM_NO : ''}</Text>
                </TouchableOpacity>
              ))}
            {persons.filter(p => p.NAME.includes(personSearch.trim())).length === 0 && (
              <Text style={styles.resultEmpty}>검색 결과 없음</Text>
            )}
          </ScrollView>
        ) : (
          <ScrollView style={styles.chipScroll} nestedScrollEnabled>
            <Text style={styles.chipSectionLabel}>구역</Text>
            <View style={styles.chipRow}>
              {personGroups.map(g => (
                <TouchableOpacity
                  key={g.area}
                  onPress={() => setExpandedArea(expandedArea === g.area ? null : g.area)}
                  style={[styles.pchip, expandedArea === g.area && styles.pchipActive]}
                  activeOpacity={0.7}
                >
                  <Text style={[styles.pchipText, expandedArea === g.area && styles.pchipTextActive]}>{g.area} ({g.list.length})</Text>
                </TouchableOpacity>
              ))}
            </View>
            {expandedArea && personGroups.filter(g => g.area === expandedArea).map(g => (
              <View key={g.area}>
                <Text style={styles.chipSectionLabel}>{g.area}</Text>
                <View style={styles.chipRow}>
                  {g.list.map(p => (
                    <TouchableOpacity
                      key={p.IDX}
                      onPress={() => setPersonFilter(String(p.IDX))}
                      style={[styles.pchip, personFilter === String(p.IDX) && styles.pchipActive]}
                      activeOpacity={0.7}
                    >
                      <Text style={[styles.pchipText, personFilter === String(p.IDX) && styles.pchipTextActive]}>{p.NAME}</Text>
                    </TouchableOpacity>
                  ))}
                </View>
              </View>
            ))}
            {personGroups.length === 0 && (
              <Text style={styles.resultEmpty}>이용자 없음</Text>
            )}
          </ScrollView>
        )}
      </View>

      <View style={styles.dateRow}>
        <View style={{ flex: 1 }}>
          <Text style={styles.label}>시작</Text>
          <TextInput value={startDate} onChangeText={setStartDate} style={styles.input} placeholder="YYYY-MM-DD" />
        </View>
        <View style={{ flex: 1 }}>
          <Text style={styles.label}>종료</Text>
          <TextInput value={endDate} onChangeText={setEndDate} style={styles.input} placeholder="YYYY-MM-DD" />
        </View>
        <TouchableOpacity onPress={load} style={styles.loadBtn}>
          <Text style={styles.loadBtnText}>조회</Text>
        </TouchableOpacity>
        {String(admin) === '0' && (
          <TouchableOpacity onPress={downloadCsv} style={styles.loadBtn}>
            <Text style={styles.loadBtnText}>CSV</Text>
          </TouchableOpacity>
        )}
      </View>

      {/* 통계 카드 */}
      <View style={styles.statRow}>
        <View style={styles.statCard}>
          <Text style={styles.statLabel}>전체</Text>
          <Text style={styles.statValue}>{records.length}건</Text>
        </View>
        <View style={styles.statCard}>
          <Text style={styles.statLabel}>소변</Text>
          <Text style={[styles.statValue, { color: C.accent }]}>{stats.urine}건</Text>
        </View>
        <View style={styles.statCard}>
          <Text style={styles.statLabel}>대변</Text>
          <Text style={[styles.statValue, { color: C.warn }]}>{stats.stool}건</Text>
        </View>
        <View style={styles.statCard}>
          <Text style={styles.statLabel}>기록자</Text>
          <Text style={styles.statValue}>{Object.keys(stats.byRecorder).length}명</Text>
        </View>
      </View>

      {/* 테이블 */}
      {loading ? (
        <ActivityIndicator color={C.accent} style={{ marginTop: s(30) }} />
      ) : (
        <ScrollView style={{ flex: 1, marginHorizontal: s(12), marginTop: s(12) }}>
          {/* 행 클릭 = 수정 요청 모달 (상단 안내) */}
          <Text style={styles.tableHint}>※ 잘못 입력된 행을 탭하면 수정 또는 삭제 요청을 보낼 수 있습니다</Text>
          <View style={styles.tableHeader}>
            <Text style={[styles.cell, styles.headerText, { flex: 0.5 }]}>No</Text>
            <Text style={[styles.cell, styles.headerText, { flex: 1.4 }]}>이름</Text>
            <Text style={[styles.cell, styles.headerText, { flex: 1 }]}>구분</Text>
            <Text style={[styles.cell, styles.headerText, { flex: 1 }]}>세부</Text>
            <Text style={[styles.cell, styles.headerText, { flex: 1.1 }]}>병증</Text>
            <Text style={[styles.cell, styles.headerText, { flex: 1 }]}>양</Text>
            <Text style={[styles.cell, styles.headerText, { flex: 1.2 }]}>기록자</Text>
            <Text style={[styles.cell, styles.headerText, { flex: 1.8 }]}>일시</Text>
            <Text style={[styles.cell, styles.headerText, { flex: 1.4 }]}>요청/수정</Text>
          </View>
          {records.length === 0 && (
            <Text style={{ textAlign: 'center', color: C.textSub, padding: s(30) }}>기록 없음</Text>
          )}
          {pageData.map((r, i0) => (
            <TouchableOpacity
              key={r.IDX}
              onPress={() => openEdit(r)}
              activeOpacity={0.65}
              style={[styles.tableRow, i0 % 2 === 1 && { backgroundColor: C.cardInner }]}
            >
              <Text style={[styles.cell, { flex: 0.5 }]}>{(_curPage - 1) * perPage + i0 + 1}</Text>
              <Text style={[styles.cell, { flex: 1.4 }]}>{r.PERSON_NAME}</Text>
              <Text style={[styles.cell, { flex: 1, color: r.TYPE === '소변' ? C.accent : C.warn }]}>{r.TYPE}</Text>
              <Text style={[styles.cell, { flex: 1 }]}>{r.SUB_TYPE || '-'}</Text>
              <Text style={[styles.cell, { flex: 1.1, color: r.STATUS && r.STATUS !== 0 ? C.err : C.textSub }]}>{fmtStatus(r.STATUS)}</Text>
              <Text style={[styles.cell, { flex: 1 }]}>{r.AMOUNT || '-'}</Text>
              <Text style={[styles.cell, { flex: 1.2 }]}>{r.RECORDER}</Text>
              <Text style={[styles.cell, { flex: 1.8, fontSize: s(12) }]}>{(r.REGDATE || '').replace('T', ' ').slice(0, 16)}</Text>
              <View style={{ flex: 1.4, flexDirection: 'row', alignItems: 'center', justifyContent: 'center', gap: s(4) }}>
                {(r.PENDING_REQ || r.EDIT_COUNT) ? (
                  <TouchableOpacity onPress={() => openReqDetail(r)} hitSlop={{ top: 6, bottom: 6, left: 6, right: 6 }}>
                    {r.PENDING_REQ ? (
                      <Text style={styles.badgePending}>요청중</Text>
                    ) : (
                      <Text style={styles.badgeEdited}>수정됨</Text>
                    )}
                  </TouchableOpacity>
                ) : null}
                <Ionicons name="create-outline" size={s(18)} color={C.warn} />
              </View>
            </TouchableOpacity>
          ))}
          {(records || []).length > 0 && (
            <TablePagination
              rowCount={(records || []).length}
              perPage={perPage}
              page={_curPage}
              onChangePage={setPage}
              onChangePerPage={(n) => { setPerPage(n); setPage(1); }}
              T={C}
              s={s}
              accent={C.accent}
              options={[50, 100, 200, 500]}
            />
          )}
        </ScrollView>
      )}

      {/* 수정 요청 모달 */}
      <Modal visible={!!editing} transparent animationType="none" onRequestClose={closeEdit}>
        <KeyboardAvoidingView
          style={styles.modalBg}
          behavior={Platform.OS === 'ios' ? 'padding' : undefined}
        >
          <View style={styles.modalCard}>
            <View style={styles.modalHeader}>
              <Text style={styles.modalTitle}>수정 · 삭제 요청</Text>
              <TouchableOpacity onPress={closeEdit} hitSlop={{ top: 12, bottom: 12, left: 12, right: 12 }}>
                <Ionicons name="close" size={s(26)} color={C.textSub} />
              </TouchableOpacity>
            </View>

            {editing && (
              <ScrollView
                style={{ maxHeight: s(500), marginRight: s(-6) }}
                contentContainerStyle={{ paddingRight: s(12), paddingBottom: s(8) }}
                showsVerticalScrollIndicator
              >
                <View style={styles.origBox}>
                  <Text style={styles.origLabel}>원본 기록</Text>
                  <Text style={styles.origText}>{editing.PERSON_NAME} · {editing.TYPE}{editing.SUB_TYPE ? '(' + editing.SUB_TYPE + ')' : ''} · 병증: {fmtStatus(editing.STATUS)} · {editing.AMOUNT || '-'}g · {editing.RECORDER}</Text>
                  <Text style={styles.origDate}>{(editing.REGDATE || '').replace('T', ' ').slice(0, 16)}</Text>
                </View>

                <Text style={styles.modalLabel}>사유 *</Text>
                <TextInput
                  value={reqReason}
                  onChangeText={setReqReason}
                  placeholder="예: 양 100 → 150 으로 잘못 기입"
                  placeholderTextColor={C.muted}
                  style={[styles.modalInput, { minHeight: s(64), textAlignVertical: 'top' }]}
                  multiline
                  maxLength={500}
                />

                <Text style={styles.modalLabel}>수정안 (필요한 항목만 입력)</Text>

                <Text style={styles.subLabel}>구분</Text>
                <View style={styles.row}>
                  {['', '소변', '대변'].map(t => (
                    <TouchableOpacity
                      key={t || 'none'}
                      onPress={() => { setReqType(t); if (t !== '대변') setReqSubType(''); }}
                      style={[styles.smallChip, reqType === t && styles.smallChipActive]}
                      activeOpacity={0.7}
                    >
                      <Text style={[styles.smallChipText, reqType === t && { color: '#FFF' }]}>{t || '변경 없음'}</Text>
                    </TouchableOpacity>
                  ))}
                </View>

                {(reqType === '대변') && (
                  <>
                    <Text style={styles.subLabel}>대변 종류</Text>
                    <View style={styles.row}>
                      {['묽은변', '보통변', '마른변'].map(st => (
                        <TouchableOpacity
                          key={st}
                          onPress={() => setReqSubType(st)}
                          style={[styles.smallChip, reqSubType === st && styles.smallChipActive]}
                          activeOpacity={0.7}
                        >
                          <Text style={[styles.smallChipText, reqSubType === st && { color: '#FFF' }]}>{st}</Text>
                        </TouchableOpacity>
                      ))}
                    </View>
                  </>
                )}

                <Text style={styles.subLabel}>병증</Text>
                <View style={styles.row}>
                  {([
                    { v: null, label: '변경 없음' },
                    { v: 0, label: '없음' },
                    { v: 1, label: '욕창' },
                    { v: 2, label: '욕창위험' },
                    { v: 4, label: '요로감염' },
                  ] as const).map(opt => (
                    <TouchableOpacity
                      key={String(opt.v)}
                      onPress={() => setReqStatus(opt.v)}
                      style={[styles.smallChip, reqStatus === opt.v && styles.smallChipActive]}
                      activeOpacity={0.7}
                    >
                      <Text style={[styles.smallChipText, reqStatus === opt.v && { color: '#FFF' }]}>{opt.label}</Text>
                    </TouchableOpacity>
                  ))}
                </View>

                <Text style={styles.subLabel}>양 (g) — 변경 시</Text>
                <TextInput
                  value={reqAmount}
                  onChangeText={(v) => setReqAmount(v.replace(/[^\d.]/g, ''))}
                  placeholder="비워두면 변경 안 함"
                  placeholderTextColor={C.muted}
                  keyboardType="numeric"
                  style={styles.modalInput}
                />

                <Text style={styles.subLabel}>기록자 이름 — 변경 시 (한글 2~5자)</Text>
                <TextInput
                  value={reqRecorder}
                  onChangeText={setReqRecorder}
                  placeholder="비워두면 변경 안 함"
                  placeholderTextColor={C.muted}
                  maxLength={5}
                  style={[
                    styles.modalInput,
                    reqRecorder && !/^[가-힣]{2,5}$/.test(reqRecorder.trim()) && { borderColor: '#F87171', borderWidth: 1.5 },
                  ]}
                />

                <Text style={styles.modalLabel}>요청자 이름 *</Text>
                <TextInput
                  value={reqRequester}
                  onChangeText={setReqRequester}
                  placeholder="예: 홍길동 (한글 2~5자)"
                  placeholderTextColor={C.muted}
                  style={[
                    styles.modalInput,
                    reqRequester && !/^[가-힣]{2,5}$/.test(reqRequester.trim()) && { borderColor: '#F87171', borderWidth: 1.5 },
                  ]}
                  maxLength={5}
                />

                {reqMsg && (
                  <View style={[styles.modalMsg, { backgroundColor: reqMsg.err ? '#F87171' : '#34D399' }]}>
                    <Text style={styles.modalMsgText}>{reqMsg.text}</Text>
                  </View>
                )}

                <TouchableOpacity
                  onPress={submitRequest}
                  style={[styles.modalSubmit, reqSubmitting && { opacity: 0.6 }]}
                  disabled={reqSubmitting}
                  activeOpacity={0.8}
                >
                  <Text style={styles.modalSubmitText}>{reqSubmitting ? '요청 중...' : '수정 요청'}</Text>
                </TouchableOpacity>

                <View style={styles.deleteDivider} />
                <Text style={styles.deleteHint}>기록 자체가 잘못 등록된 경우 — 사유·요청자만 입력 후 삭제를 요청하세요 (관리자 승인 후 제거).</Text>
                <TouchableOpacity
                  onPress={submitDelete}
                  style={[styles.modalDelete, reqSubmitting && { opacity: 0.6 }]}
                  disabled={reqSubmitting}
                  activeOpacity={0.8}
                >
                  <Ionicons name="trash-outline" size={s(17)} color="#FFF" />
                  <Text style={styles.modalDeleteText}>이 기록 삭제 요청</Text>
                </TouchableOpacity>
              </ScrollView>
            )}
          </View>
        </KeyboardAvoidingView>
      </Modal>

      {/* 요청 / 수정 내역 상세 (변경 전 → 후) */}
      <Modal visible={!!reqDetailRec} transparent animationType="fade" onRequestClose={() => setReqDetailRec(null)}>
        <View style={styles.modalBg}>
          <View style={[styles.modalCard, { maxHeight: '85%' }]}>
            <View style={styles.modalHeader}>
              <Text style={styles.modalTitle}>요청 / 수정 내역</Text>
              <TouchableOpacity onPress={() => setReqDetailRec(null)} hitSlop={{ top: 12, bottom: 12, left: 12, right: 12 }}>
                <Ionicons name="close" size={s(26)} color={C.textSub} />
              </TouchableOpacity>
            </View>
            {reqDetailRec && (
              <View style={styles.origBox}>
                <Text style={styles.origLabel}>현재 기록</Text>
                <Text style={styles.origText}>{reqDetailRec.PERSON_NAME} · {reqDetailRec.TYPE}{reqDetailRec.SUB_TYPE ? '(' + reqDetailRec.SUB_TYPE + ')' : ''} · 병증 {fmtStatus(reqDetailRec.STATUS)} · {reqDetailRec.AMOUNT || '-'}g · {reqDetailRec.RECORDER}</Text>
              </View>
            )}
            {reqDetailLoading ? (
              <ActivityIndicator style={{ marginVertical: s(24) }} color={C.accent} />
            ) : reqDetailData.length === 0 ? (
              <Text style={{ color: C.textSub, textAlign: 'center', padding: s(24) }}>요청 내역이 없습니다</Text>
            ) : (
              <ScrollView style={{ marginTop: s(4) }}>
                {reqDetailData.map((rq) => (
                  <View key={rq.IDX} style={styles.reqItem}>
                    <View style={{ flexDirection: 'row', alignItems: 'center', gap: s(6), marginBottom: s(6), flexWrap: 'wrap' }}>
                      <Text style={rq.STATUS === 'pending' ? styles.badgePending : rq.STATUS === 'approved' ? styles.badgeEdited : styles.badgeRejected}>
                        {rq.STATUS === 'pending' ? '요청중' : rq.STATUS === 'approved' ? '승인됨' : '거부됨'}
                      </Text>
                      <Text style={{ color: C.textSub, fontSize: s(12), fontWeight: '600' }}>
                        {rq.REQ_TYPE === 'delete' ? '삭제 요청' : '수정 요청'} · {rq.REQUESTER}
                      </Text>
                    </View>
                    <Text style={{ color: C.text, fontSize: s(13), marginBottom: s(4) }}>사유: {rq.REASON}</Text>
                    {rq.REQ_TYPE === 'edit' && (
                      <View style={styles.diffBox}>
                        {rq.NEW_TYPE != null && diffLine('구분', rq.OLD_TYPE, rq.NEW_TYPE, s, styles)}
                        {rq.NEW_SUB_TYPE != null && diffLine('세부', rq.OLD_SUB_TYPE, rq.NEW_SUB_TYPE, s, styles)}
                        {rq.NEW_STATUS != null && diffLine('병증', fmtStatus(rq.OLD_STATUS), fmtStatus(rq.NEW_STATUS), s, styles)}
                        {rq.NEW_AMOUNT != null && diffLine('양(g)', rq.OLD_AMOUNT, rq.NEW_AMOUNT, s, styles)}
                        {rq.NEW_RECORDER != null && diffLine('기록자', rq.OLD_RECORDER, rq.NEW_RECORDER, s, styles)}
                      </View>
                    )}
                    <Text style={{ color: C.muted, fontSize: s(11), marginTop: s(6) }}>
                      요청 {(rq.REGDATE || '').replace('T', ' ').slice(0, 16)}
                      {rq.RESOLVED_AT ? '   ·   처리 ' + (rq.RESOLVED_AT || '').replace('T', ' ').slice(0, 16) + (rq.RESOLVER ? ' (' + rq.RESOLVER + ')' : '') : ''}
                    </Text>
                    {rq.ADMIN_MEMO ? <Text style={{ color: C.textSub, fontSize: s(11), marginTop: s(2) }}>메모: {rq.ADMIN_MEMO}</Text> : null}
                  </View>
                ))}
              </ScrollView>
            )}
          </View>
        </View>
      </Modal>
    </View>
  );
}

function diffLine(label: string, oldV: any, newV: any, s: ScaleFn, styles: any) {
  return (
    <View style={styles.diffRow} key={label}>
      <Text style={{ color: C.textSub, fontSize: s(12), width: s(54) }}>{label}</Text>
      <Text style={{ color: C.muted, fontSize: s(12) }}>{oldV ?? '-'}</Text>
      <Text style={{ color: C.accent, fontSize: s(12), fontWeight: '700' }}>{'  →  '}</Text>
      <Text style={{ color: C.ok, fontSize: s(12), fontWeight: '700' }}>{newV ?? '-'}</Text>
    </View>
  );
}

const makeStyles = (s: ScaleFn) => StyleSheet.create({
  root: { flex: 1 },
  header: { flexDirection: 'row', justifyContent: 'space-between', alignItems: 'center', padding: s(16), paddingBottom: s(8) },
  title: { fontSize: s(20), fontWeight: '700', color: C.text },
  csvBtn: { flexDirection: 'row', alignItems: 'center', backgroundColor: C.accent, paddingHorizontal: s(12), paddingVertical: s(8), borderRadius: s(8), gap: s(4) },
  csvBtnText: { color: '#FFF', fontWeight: '600', fontSize: s(13) },
  filterRow: { paddingHorizontal: s(12) },
  label: { fontSize: s(12), color: C.textSub, marginBottom: s(4), fontWeight: '600' },
  selectWrap: { paddingVertical: s(4) },
  chip: { paddingHorizontal: s(12), paddingVertical: s(6), borderRadius: s(14), backgroundColor: C.card, borderWidth: 1, borderColor: C.border, marginRight: s(6) },
  chipActive: { backgroundColor: C.accent, borderColor: C.accent },
  chipText: { color: C.textSub, fontSize: s(13), fontWeight: '600' },
  chipTextActive: { color: '#FFF' },
  dateRow: { flexDirection: 'row', alignItems: 'flex-end', padding: s(12), gap: s(8) },
  input: { borderWidth: 1, borderColor: C.border, borderRadius: s(8), padding: s(8), fontSize: s(14), color: C.text, backgroundColor: C.card },
  loadBtn: { backgroundColor: C.text, paddingHorizontal: s(16), paddingVertical: s(9), borderRadius: s(8) },
  loadBtnText: { color: '#FFF', fontWeight: '700', fontSize: s(14) },
  statRow: { flexDirection: 'row', gap: s(8), paddingHorizontal: s(12), marginTop: s(4) },
  statCard: { flex: 1, backgroundColor: C.card, borderWidth: 1, borderColor: C.border, borderRadius: s(10), padding: s(10), alignItems: 'center' },
  statLabel: { fontSize: s(12), color: C.textSub, fontWeight: '600' },
  statValue: { fontSize: s(18), color: C.text, fontWeight: '700', marginTop: s(4) },
  tableHeader: { flexDirection: 'row', backgroundColor: C.text, paddingVertical: s(10), borderTopLeftRadius: s(8), borderTopRightRadius: s(8) },
  headerText: { color: '#FFF', fontWeight: '700' },
  tableRow: { flexDirection: 'row', paddingVertical: s(8), borderBottomWidth: 1, borderBottomColor: C.border, alignItems: 'center' },
  cell: { fontSize: s(13), color: C.text, textAlign: 'center', paddingHorizontal: s(4) },
  tableHint: { fontSize: s(12), color: C.textSub, marginBottom: s(6), paddingHorizontal: s(4) },
  userSection: { backgroundColor: C.card, marginHorizontal: s(12), marginTop: s(8), padding: s(14), borderRadius: s(12), borderWidth: 1, borderColor: C.border },
  userSectionHeader: { flexDirection: 'row', alignItems: 'center', marginBottom: s(10), gap: s(8) },
  userSectionTitle: { fontSize: s(17), fontWeight: '700', color: C.text, flex: 1 },
  selectedBadge: { flexDirection: 'row', alignItems: 'center', backgroundColor: C.accent, paddingHorizontal: s(10), paddingVertical: s(5), borderRadius: s(14), gap: s(6) },
  selectedBadgeText: { color: '#FFF', fontWeight: '700', fontSize: s(13) },
  userSearch: { borderWidth: 1.5, borderColor: C.accent, borderRadius: s(10), paddingHorizontal: s(14), paddingVertical: s(12), fontSize: s(16), color: C.text, backgroundColor: C.cardInner, marginBottom: s(10) },
  userChipWrap: { paddingVertical: s(2) },
  resultList: { marginTop: s(8), borderWidth: 1, borderColor: C.border, borderRadius: s(10), backgroundColor: C.card, maxHeight: s(260) },
  resultRow: { flexDirection: 'row', justifyContent: 'space-between', alignItems: 'center', paddingHorizontal: s(14), paddingVertical: s(12), borderBottomWidth: 1, borderBottomColor: C.border },
  resultRowActive: { backgroundColor: C.cardInner },
  resultName: { fontSize: s(16), fontWeight: '700', color: C.text },
  resultMeta: { fontSize: s(13), color: C.textSub },
  resultEmpty: { color: C.muted, padding: s(12) },
  chipScroll: { maxHeight: s(320), marginTop: s(2) },
  chipSectionLabel: { fontSize: s(12), fontWeight: '700', color: C.textSub, marginTop: s(10), marginBottom: s(6) },
  chipRow: { flexDirection: 'row', flexWrap: 'wrap' },
  pchip: { paddingHorizontal: s(14), paddingVertical: s(9), borderRadius: s(16), backgroundColor: C.cardInner, borderWidth: 1, borderColor: C.border, marginRight: s(8), marginBottom: s(8) },
  pchipActive: { backgroundColor: C.accent, borderColor: C.accent },
  pchipText: { fontSize: s(14), color: C.text, fontWeight: '600' },
  pchipTextActive: { color: '#FFF', fontWeight: '700' },
  userChip: { paddingHorizontal: s(16), paddingVertical: s(10), borderRadius: s(18), backgroundColor: C.cardInner, borderWidth: 1, borderColor: C.border, marginRight: s(8), minHeight: s(40), justifyContent: 'center' },
  userChipActive: { backgroundColor: C.accent, borderColor: C.accent },
  userChipText: { color: C.text, fontSize: s(15), fontWeight: '600' },
  userChipTextActive: { color: '#FFF', fontWeight: '700' },
  // 수정 요청 모달
  modalBg: { flex: 1, backgroundColor: 'rgba(15,23,42,0.55)', justifyContent: 'center', alignItems: 'center', padding: s(16) },
  modalCard: { width: '100%', maxWidth: s(500), backgroundColor: C.card, borderRadius: s(14), padding: s(18), paddingRight: s(12), shadowColor: '#000', shadowOpacity: 0.3, shadowRadius: s(16), shadowOffset: { width: 0, height: 8 }, elevation: 6, overflow: 'hidden' },
  modalHeader: { flexDirection: 'row', justifyContent: 'space-between', alignItems: 'center', marginBottom: s(12), paddingBottom: s(10), borderBottomWidth: 1, borderBottomColor: C.border },
  modalTitle: { fontSize: s(19), fontWeight: '700', color: C.text },
  origBox: { backgroundColor: C.cardInner, padding: s(12), borderRadius: s(10), marginBottom: s(14) },
  origLabel: { fontSize: s(11), color: C.textSub, fontWeight: '700', marginBottom: s(4) },
  origText: { fontSize: s(14), color: C.text, fontWeight: '600' },
  origDate: { fontSize: s(12), color: C.textSub, marginTop: s(4) },
  modalLabel: { fontSize: s(14), color: C.textSub, marginTop: s(12), marginBottom: s(6), fontWeight: '700' },
  subLabel: { fontSize: s(12), color: C.muted, marginTop: s(10), marginBottom: s(4), fontWeight: '600' },
  modalInput: { borderWidth: 1, borderColor: C.border, borderRadius: s(8), paddingHorizontal: s(12), paddingVertical: s(10), fontSize: s(15), color: C.text, backgroundColor: C.cardInner },
  row: { flexDirection: 'row', gap: s(8), flexWrap: 'wrap' },
  smallChip: { paddingHorizontal: s(14), paddingVertical: s(10), borderRadius: s(8), borderWidth: 1, borderColor: C.border, backgroundColor: C.cardInner },
  smallChipActive: { backgroundColor: C.accent, borderColor: C.accent },
  smallChipText: { fontSize: s(13), color: C.textSub, fontWeight: '600' },
  modalMsg: { marginTop: s(14), padding: s(10), borderRadius: s(8), alignItems: 'center' },
  modalMsgText: { color: '#FFF', fontWeight: '700', fontSize: s(14) },
  // 요청중/수정됨 뱃지 + 상세 내역
  badgePending: { fontSize: s(11), fontWeight: '700', color: '#FFF', backgroundColor: C.warn, paddingHorizontal: s(7), paddingVertical: s(2), borderRadius: s(6), overflow: 'hidden' },
  badgeEdited: { fontSize: s(11), fontWeight: '700', color: '#FFF', backgroundColor: C.accent, paddingHorizontal: s(7), paddingVertical: s(2), borderRadius: s(6), overflow: 'hidden' },
  badgeRejected: { fontSize: s(11), fontWeight: '700', color: '#FFF', backgroundColor: C.muted, paddingHorizontal: s(7), paddingVertical: s(2), borderRadius: s(6), overflow: 'hidden' },
  reqItem: { backgroundColor: C.cardInner, borderRadius: s(10), padding: s(12), marginBottom: s(10), borderWidth: 1, borderColor: C.border },
  diffBox: { backgroundColor: C.card, borderRadius: s(8), padding: s(8), marginTop: s(4) },
  diffRow: { flexDirection: 'row', alignItems: 'center', marginBottom: s(2) },
  modalSubmit: { marginTop: s(16), backgroundColor: C.warn, paddingVertical: s(14), borderRadius: s(10), alignItems: 'center' },
  modalSubmitText: { color: '#FFF', fontSize: s(16), fontWeight: '700' },
  deleteDivider: { height: s(1), backgroundColor: C.border, marginTop: s(18), marginBottom: s(12) },
  deleteHint: { fontSize: s(12), color: C.muted, marginBottom: s(8), lineHeight: s(17) },
  modalDelete: { flexDirection: 'row', justifyContent: 'center', alignItems: 'center', gap: s(6), backgroundColor: C.err, paddingVertical: s(13), borderRadius: s(10) },
  modalDeleteText: { color: '#FFF', fontSize: s(15), fontWeight: '700' },
});
